/** TradingView Paper Trading Order History CSV import (`broker_name: tradingview_paper`). */

export const TRADINGVIEW_PAPER_BROKER_SLUG = "tradingview_paper";

export const TRADINGVIEW_PAPER_DEFAULT_EXECUTING_BROKER = "Paper";

export const TRADINGVIEW_PAPER_DEFAULT_ASSET_CLASSES = [
  "commodity",
  "crypto",
  "energy",
  "equity",
  "fx",
  "index",
  "metal",
] as const;

export function isTradingViewPaperBroker(brokerName: string | null | undefined): boolean {
  return String(brokerName || "").toLowerCase() === TRADINGVIEW_PAPER_BROKER_SLUG;
}

/** Hidden on the form — always sent as `tradingview_paper` on import. Instrument is never used. */
export function isTradingViewPaperHiddenCsvField(field: string): boolean {
  const lower = field.toLowerCase();
  return lower === "broker_name" || lower === "instrument" || lower === "import_instrument";
}

/** User-facing CSV field labels — overrides technical API `field_labels`. */
export const TRADINGVIEW_PAPER_CSV_FIELD_LABELS: Record<string, string> = {
  account_name: "Account name",
  time_zone: "Timezone",
  traded_asset_classes: "Asset class",
  executing_broker_name: "Executing broker",
};

export function tradingViewPaperCsvFieldLabel(field: string, fallback: string): string {
  return TRADINGVIEW_PAPER_CSV_FIELD_LABELS[field] ?? fallback;
}

function normalizeAssetClasses(classes: string[]): string[] {
  return classes.map((c) => c.trim().toLowerCase()).filter(Boolean);
}

export function resolveTradingViewPaperCsvAssetClasses(
  values: Record<string, string>,
  selectedClasses: string[],
  fixedValues?: string[] | null
): string[] {
  const fromPicker = normalizeAssetClasses(selectedClasses);
  if (fromPicker.length > 0) return fromPicker;
  const single = (values.traded_asset_classes || values.tradedAssetClasses || "").trim();
  if (single) return [single.toLowerCase()];
  if (fixedValues?.length) return normalizeAssetClasses(fixedValues);
  return [];
}

/**
 * Query params for POST /broker/tradingview-paper/csv-import.
 * No instrument — symbols come from the Order History CSV.
 */
export function buildTradingViewPaperCsvImportParams(
  values: Record<string, string>,
  assetClasses: string[] = []
): Record<string, string | string[]> {
  const accountName = (values.account_name || values.accountName || "").trim();
  const timeZone = (values.time_zone || values.timeZone || "").trim();
  const executingBrokerName = (
    values.executing_broker_name ||
    values.executingBrokerName ||
    TRADINGVIEW_PAPER_DEFAULT_EXECUTING_BROKER
  ).trim();
  const params: Record<string, string | string[]> = {
    broker_name: TRADINGVIEW_PAPER_BROKER_SLUG,
    account_name: accountName,
    executing_broker_name: executingBrokerName || TRADINGVIEW_PAPER_DEFAULT_EXECUTING_BROKER,
    time_zone: timeZone,
  };
  const tradedAssetClasses = resolveTradingViewPaperCsvAssetClasses(values, assetClasses);
  if (tradedAssetClasses.length > 0) {
    params.traded_asset_classes = tradedAssetClasses;
  }
  return params;
}

/** Strategy Tester List of Trades — belongs on the Backtest lane, not Paper. */
export function looksLikeTradingViewStrategyTesterCsv(headerLine: string): boolean {
  const h = headerLine.toLowerCase();
  return h.includes("trade #") || (h.includes("date and time") && h.includes("position size"));
}

export function tradingViewPaperWrongFileHint(): string {
  return "This looks like a Strategy Tester List of Trades export. Use the TradingView Backtest lane for that file, or export Paper Trading → Order History instead.";
}

export type TradingViewPaperImportMeta = {
  round_trips?: number;
  fills_parsed?: number;
  fills_skipped?: number;
  brackets_attached?: number;
  symbols_seen?: string[];
  open_lots_remaining?: number;
};

function readPaperImportMeta(payload: unknown): TradingViewPaperImportMeta | null {
  if (!payload || typeof payload !== "object") return null;
  const raw = payload as Record<string, unknown>;
  const meta = (raw.report_meta ?? raw.reportMeta) as Record<string, unknown> | undefined;
  if (!meta || typeof meta !== "object") return null;
  return meta as TradingViewPaperImportMeta;
}

/** Optional toast line from `report_meta` after a successful paper import. */
export function formatTradingViewPaperImportSuccessDescription(payload: unknown): string | null {
  const meta = readPaperImportMeta(payload);
  if (!meta) return null;
  const parts: string[] = [];
  const roundTrips = meta.round_trips;
  if (typeof roundTrips === "number") {
    parts.push(`${roundTrips} trade${roundTrips === 1 ? "" : "s"}`);
  }
  const brackets = meta.brackets_attached;
  if (typeof brackets === "number" && brackets > 0) {
    parts.push(`${brackets} with SL/TP`);
  }
  const symbols = Array.isArray(meta.symbols_seen)
    ? meta.symbols_seen.map((s) => String(s).trim()).filter(Boolean)
    : [];
  if (symbols.length > 0) {
    const preview = symbols.slice(0, 4).join(", ");
    parts.push(
      symbols.length > 4
        ? `symbols: ${preview} (+${symbols.length - 4})`
        : `symbols: ${preview}`
    );
  }
  return parts.length ? parts.join(" · ") : null;
}

export function formatTradingViewPaperOpenLotsWarning(payload: unknown): string | null {
  const meta = readPaperImportMeta(payload);
  const open = meta?.open_lots_remaining;
  if (typeof open !== "number" || open <= 0) return null;
  return `${open} unmatched open lot${open === 1 ? "" : "s"} remain in the file (no closing fill yet). Those positions are not stored as closed trades.`;
}
